import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Badge,
  Card,
  EmailInput,
  FilterBar,
  MultiSelect,
  Page,
  SimpleSelect,
  SwitchInput,
  TextInput,
} from 'corso-admin-ui';
import { CrewMerchantUi, isEnum, ReportableEntity } from 'corso-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
  Filter,
  useFilterDefinition,
} from '~/components/searchTable/searchControlBar/useFilterDefinition';
import { useCustomFields } from '~/features/settings/hooks/useCustomFields';
import {
  useReportDefinition,
  useReportDefinitionUpsert,
} from '~/features/settings/hooks/useReportDefinitions';
import { usePathParams } from '~/hooks/usePathParams';
import { useSearchParam } from '~/hooks/useSearchParam';
import { useStoreId } from '~/providers/StoreUserProvider';
import {
  columnsByEntity,
  exportColumnDisplayName,
  isClaimEntity,
} from '~/utils/dataExport';
import { numberIdsToOptions, optionsToNumberIds } from '~/utils/form';
import {
  buildDefaults,
  definitionToFormValues,
  entityOptions,
  exportMonthlyOptions,
  exportWeeklyOptions,
  frequencyOptions,
  reportDefinitionFormSchema,
} from './dataExportFormUtils';

function FiltersCard({ entity }: { entity: ReportableEntity }) {
  const { setValue, watch } =
    useFormContext<z.infer<typeof reportDefinitionFormSchema>>();
  const filterDefinition = useFilterDefinition(entity);
  const filtersValue = watch('filters');

  const filterControls = useMemo(() => {
    const { searchTerm, ...rest } = filterDefinition.filters;
    return rest;
  }, [filterDefinition.filters]);

  const handleFilterChange = (changed: Filter) => {
    setValue(
      'filters',
      { ...filtersValue, [changed.id]: changed.value },
      { shouldDirty: true },
    );
  };

  const handleRemoveFilters = (ids?: Filter['id'][]) => {
    if (!ids) {
      setValue('filters', {}, { shouldDirty: true });
      return;
    }
    const removed = new Set<string>(ids);
    const next = Object.fromEntries(
      Object.entries(filtersValue).filter(([k]) => !removed.has(k)),
    );
    setValue('filters', next, { shouldDirty: true });
  };

  const hasDateFilter = 'createdOn' in filtersValue && !!filtersValue.createdOn;

  return (
    <Card>
      <Card.Heading>Filters</Card.Heading>
      {!hasDateFilter && (
        <Alert heading="No date filter applied, defaulting to last 30 days." />
      )}
      <FilterBar
        filters={filterControls}
        activeFilters={filtersValue}
        onChange={handleFilterChange}
        onRemoveFilters={handleRemoveFilters}
      />
    </Card>
  );
}

function useDefinition({ entity }: { entity: ReportableEntity }) {
  const { dataExportDefinitionId: definitionId } = usePathParams(
    z.object({ dataExportDefinitionId: z.coerce.number().optional() }),
  );

  const { data } = useReportDefinition(entity, definitionId);
  return data;
}

export default function DataExportSettingsFormPage() {
  const storeId = useStoreId();
  const navigate = useNavigate();

  const entityParam = useSearchParam('entity');
  const initialEntity = isEnum(entityParam, ReportableEntity)
    ? entityParam
    : 'Return';

  // The entity used to fetch the definition must come from the URL — the
  // server keys the resource on `/reports/:entity/definitions/:id`, and the
  // form's `kind` isn't populated until the fetch resolves.
  const definition = useDefinition({ entity: initialEntity });

  const methods = useForm({
    resolver: zodResolver(reportDefinitionFormSchema),
    defaultValues: buildDefaults(initialEntity),
  });

  const entity = methods.watch('kind');
  const selectedFrequency = methods.watch('frequency');

  const [showSchedule, setShowSchedule] = useState(false);

  useEffect(() => {
    if (definition) {
      setShowSchedule(!!definition.frequency);
      methods.reset(definitionToFormValues(definition));
    }
  }, [definition, methods]);

  // upsert the mutation
  const { mutateAsync: upsert, isPending: isSaving } =
    useReportDefinitionUpsert();

  // construct return to for the on submit
  const returnTo =
    useSearchParam('returnTo') ?? `/${storeId}/settings/data-export`;

  const onSubmit = methods.handleSubmit(async (values) => {
    const parsed = CrewMerchantUi.reportDefinitionCreate.parse(values);
    const result = await upsert(
      definition ? { ...parsed, id: definition.id } : parsed,
    );
    navigate({ pathname: returnTo, hash: `data-export-${result.id}` });
  });

  // construct the column and custom field options
  const columnOptions = Object.values(columnsByEntity[entity]).map((value) => ({
    label: exportColumnDisplayName[value],
    value,
  }));

  const { data: customFields = [] } = useCustomFields();
  const customFieldOptions = customFields.map((cf) => ({
    label: cf.displayName,
    value: `${cf.id}`,
  }));

  const handleToggleShowSchedule = (checked: boolean) => {
    setShowSchedule(checked);
    if (!checked) {
      methods.setValue('frequency', undefined);
      methods.setValue('exportDay', undefined);
      methods.setValue('exportEmails', '');
    }
  };
  return (
    <Page
      backAction={{ accessibilityLabel: 'Back', to: returnTo }}
      title={`${definition ? 'Update' : 'Create'} Data Export Template`}
    >
      <FormProvider {...methods}>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            onSubmit(e).catch(() => undefined);
          }}
        >
          <Card>
            <Card.Heading>Details</Card.Heading>
            <TextInput
              id="name"
              label="Name"
              required
              {...methods.register('name')}
              error={methods.formState.errors.name?.message}
            />
            {definition ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-700">
                  Type
                </span>
                <Badge>{entity}</Badge>
              </div>
            ) : (
              <SimpleSelect
                label="Type"
                options={entityOptions}
                value={entity}
                onChange={(next) => {
                  methods.reset(buildDefaults(next));
                }}
              />
            )}
          </Card>

          <FiltersCard entity={entity} />

          <Card>
            <Card.Heading>CSV output</Card.Heading>
            <Controller
              name="jsonDefinition.columns"
              control={methods.control}
              render={({ field, fieldState }) => (
                <MultiSelect
                  togglable
                  label="Columns"
                  placeholder="Select columns"
                  options={columnOptions}
                  value={field.value.map((value) => ({
                    label:
                      columnOptions.find((o) => o.value === value)?.label ??
                      value,
                    value,
                  }))}
                  onChange={(next) => field.onChange(next.map((v) => v.value))}
                  error={fieldState.error?.message}
                />
              )}
            />

            {isClaimEntity(entity) && (
              <>
                <Controller
                  name="jsonDefinition.lineItemCustomFieldIds"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <MultiSelect
                      togglable
                      label="Line item custom fields"
                      placeholder="Select custom fields"
                      options={customFieldOptions}
                      value={numberIdsToOptions(
                        field.value ?? [],
                        customFieldOptions,
                      )}
                      onChange={(next) =>
                        field.onChange(optionsToNumberIds(next))
                      }
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="jsonDefinition.inspectionCustomFieldIds"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <MultiSelect
                      togglable
                      label="Inspection custom fields"
                      placeholder="Select custom fields"
                      options={customFieldOptions}
                      value={numberIdsToOptions(
                        field.value ?? [],
                        customFieldOptions,
                      )}
                      onChange={(next) =>
                        field.onChange(optionsToNumberIds(next))
                      }
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </>
            )}

            {entity === 'Registration' && (
              <Controller
                name="jsonDefinition.registrationCustomFieldIds"
                control={methods.control}
                render={({ field, fieldState }) => (
                  <MultiSelect
                    togglable
                    label="Registration custom fields"
                    placeholder="Select custom fields"
                    options={customFieldOptions}
                    value={numberIdsToOptions(
                      field.value ?? [],
                      customFieldOptions,
                    )}
                    onChange={(next) =>
                      field.onChange(optionsToNumberIds(next))
                    }
                    error={fieldState.error?.message}
                  />
                )}
              />
            )}
          </Card>

          <Card>
            <Card.Line>
              <div>
                <Card.Heading>Schedule</Card.Heading>
                <p className="text-xs text-neutral-600">
                  Optionally configure a schedule to automatically run this
                  export on a recurring basis.
                </p>
              </div>

              <SwitchInput
                id="enable-schedule"
                label="Enable schedule"
                labelVisuallyHidden
                checked={showSchedule}
                onChange={(e) => {
                  handleToggleShowSchedule(e.target.checked);
                }}
              />
            </Card.Line>
            {showSchedule && (
              <>
                <Controller
                  name="frequency"
                  control={methods.control}
                  render={({ field }) => (
                    <SimpleSelect
                      label="Frequency"
                      options={frequencyOptions}
                      value={field.value ?? null}
                      onChange={(next) => field.onChange(next)}
                    />
                  )}
                />
                {selectedFrequency !== 'Daily' && (
                  <Controller
                    name="exportDay"
                    control={methods.control}
                    render={({ field }) => (
                      <SimpleSelect
                        label="Day"
                        options={
                          selectedFrequency === 'Weekly'
                            ? exportWeeklyOptions
                            : exportMonthlyOptions
                        }
                        value={field.value == null ? null : `${field.value}`}
                        onChange={(next) =>
                          field.onChange(
                            next ? Number.parseInt(next, 10) : null,
                          )
                        }
                      />
                    )}
                  />
                )}

                <EmailInput
                  multiple
                  id="exportEmails"
                  label="Notification emails"
                  details="Enter a comma-separated list of emails to receive notifications when the export is generated."
                  {...methods.register('exportEmails')}
                  error={methods.formState.errors.exportEmails?.message}
                />
              </>
            )}
          </Card>

          <Card>
            <div className="flex flex-col gap-2 md:flex-row">
              <Action onClick={() => navigate(returnTo)}>Cancel</Action>

              <Action variant="primary" type="submit" loading={isSaving}>
                {definition ? 'Save' : 'Create'}
              </Action>
            </div>
          </Card>
        </form>
      </FormProvider>
    </Page>
  );
}
